/* eslint-disable react-refresh/only-export-components */
import { HTTPError } from "ky";
import { ADVANCED_TEXT_OPERATORS, AdvancedTextOperator, DATE_PRESETS_TYPE } from "./constants";
import { dateToISODateOnly, formatNumberBR } from "./maskUtils";
import {
	differenceInCalendarDays,
	endOfMonth,
	endOfWeek,
	getDaysInMonth,
	isValid,
	parseISO,
	startOfMonth,
	startOfWeek,
	subDays,
	subMonths,
} from "date-fns";
import { JSX } from "react";
import { Tooltip } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";

/**
 * Verifica se a string contém apenas dígitos.
 * @param v - string de entrada
 * @returns true se a string contiver apenas dígitos
 */
export const isOnlyDigits = (v: string) => /^\d+$/.test(v);

/**
 * Verifica se a string representa um número inteiro com sinal opcional.
 * @param v - string de entrada
 * @returns true se a string for um inteiro com sinal negativo opcional
 */
export const isSignedInteger = (v: string) => /^-?\d+$/.test(v);

/**
 * Normaliza uma string removendo espaços em branco das extremidades e convertendo para minúsculas.
 * @param str - string de entrada
 * @returns string normalizada
 */
export const normalizeStr = (str: string) => (str ?? "").trim().toLowerCase();

/**
 * Obtém o código de status HTTP a partir de um HTTPError.
 * @param err - objeto de erro
 * @returns código de status ou undefined caso não seja um HTTPError
 */
export const getStatusError = (err: unknown) => (err instanceof HTTPError ? err.response.status : undefined);

/**
 * Normaliza um valor para uma string sem espaços nas extremidades.
 * @param valor - valor de entrada
 * @param lowercase - se deve converter a string para minúsculas
 * @returns string tratada para valores do tipo string, number e boolean; string vazia para os demais casos
 */
export const normalizarTexto = (valor: unknown, lowercase = false): string => {
	const texto =
		typeof valor === "string" || typeof valor === "number" || typeof valor === "boolean"
			? String(valor).trim()
			: "";
	return lowercase ? texto.toLowerCase() : texto;
};

/**
 * Normaliza um valor de texto para comparação removendo acentos e convertendo para maiúsculas.
 * @param valor - string de entrada
 * @returns string normalizada em maiúsculas sem diacríticos
 */
export function normalizarTextoComparacao(valor: string): string {
	return valor
		.trim()
		.toUpperCase()
		.normalize("NFD")
		.replaceAll(/[\u0300-\u036f]/g, "");
}

/**
 * Normaliza texto multilinha vindo de APIs ou campos externos.
 *
 * Converte separadores escapados e reais para `\n`, limita sequências a uma
 * linha vazia entre blocos e remove espaços das extremidades do texto.
 */
export function normalizarTextoMultilinha(valor: string | null): string | null {
	if (valor === null) return null;

	return valor
		.replaceAll(String.raw`\r\n`, "\n")
		.replaceAll(String.raw`\n`, "\n")
		.replaceAll(String.raw`\r`, "\n")
		.replaceAll(/\r\n?/g, "\n")
		.replaceAll(/\n{3,}/g, "\n\n")
		.trim();
}

/**
 * Normaliza um valor numérico em um número finito.
 * @param valor - número de entrada ou valor nulo/indefinido
 * @returns número finito ou zero se o valor não for finito
 */
export function normalizarNumero(valor: number | null | undefined): number {
	const numero = Number(valor ?? 0);
	return Number.isFinite(numero) ? numero : 0;
}

/**
 * Normaliza um identificador numérico vindo de API, form ou rota, aceitando texto numérico.
 * @param valor - número ou string a normalizar
 * @returns inteiro maior que zero ou `null` quando o valor não serve como id
 */
export function normalizarInteiroPositivo(valor: unknown): number | null {
	if (typeof valor !== "number" && typeof valor !== "string") return null;

	const numero = Number(valor);
	return Number.isSafeInteger(numero) && numero > 0 ? numero : null;
}

type serializeCSVProps = ReadonlyArray<string | number | null | undefined>;
/**
 * Serializa um array de valores em uma string separada por vírgulas.
 * @param valores - valores para serializar
 * @returns string separada por vírgulas dos valores normalizados
 */
export function serializarCsv(valores: serializeCSVProps): string {
	return valores
		.map((valor) => normalizarTexto(valor))
		.filter(Boolean)
		.join(",");
}

/**
 * Desserializa uma string separada por vírgulas em um array de valores limpos.
 * Contraparte de {@link serializarCsv}.
 * @param valor - string separada por vírgulas
 * @returns lista de valores sem espaços nas extremidades e sem entradas vazias
 */
export function desserializarCsv(valor: string | null | undefined): string[] {
	return normalizarTexto(valor)
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean);
}

/**
 * Formata um valor sensível para exibição, opcionalmente ocultando-o.
 * @param value - valor a ser formatado
 * @param show - se deve revelar o valor
 * @returns uma string ou elemento JSX representando o valor formatado
 */
export function formatarSensitiveValue(
	value: string | number | null | undefined,
	show?: boolean,
): string | JSX.Element {
	const strValue = normalizarTexto(value);
	if (!strValue) return "—";
	if (show) return strValue;

	return (
		<>
			<span aria-hidden="true" className="inline-block select-none blur-xs transition-[filter] duration-150">
				{strValue}
			</span>
			<span className="sr-only">Valor oculto</span>
		</>
	);
}

export type ValorParametroConsulta = string | number | boolean | null | undefined;

/**
 * Adiciona um parâmetro opcional aos parâmetros de consulta da requisição.
 *
 * Ignora valores null, undefined e strings vazias.
 * Aceita valores simples ou arrays.
 *
 * Exemplos:
 * - status: ["A", "B"] vira ?status=A&status=B
 * - ativo: false vira ?ativo=false
 * - valor: 0 vira ?valor=0
 */
export function adicionarParametroConsulta(
	parametrosConsulta: URLSearchParams,
	chave: string,
	valor: ValorParametroConsulta | ValorParametroConsulta[],
) {
	if (valor === undefined || valor === null) return;

	if (Array.isArray(valor)) {
		valor.forEach((item) => {
			adicionarParametroConsulta(parametrosConsulta, chave, item);
		});

		return;
	}

	const valorNormalizado = typeof valor === "string" ? valor.trim() : String(valor);

	if (!valorNormalizado) return;

	parametrosConsulta.append(chave, valorNormalizado);
}

/**
 * Normaliza o operador de pesquisa avançada para um valor válido.
 *
 * @param filtro - Operador de texto opcional a ser normalizado.
 * @returns O operador normalizado se suportado, caso contrário retorna `ilike`.
 */
export function normalizarOperadorAvancado(filtro?: string): AdvancedTextOperator {
	const operador = String(filtro ?? "")
		.toLowerCase()
		.trim();
	if (ADVANCED_TEXT_OPERATORS.includes(operador as AdvancedTextOperator)) return operador as AdvancedTextOperator;
	return "ilike";
}

/**
 * Normaliza um período em array garantindo que ambas as datas existam.
 *
 * @param periodo - Array com data inicial e final, ou `null`/`undefined`.
 * @returns O mesmo período quando válido, ou `null` quando alguma data estiver ausente.
 */
export function normalizarPeriodoArray(
	periodo: [string | null, string | null] | null | undefined,
): [string, string] | null {
	const [dataInicial, dataFinal] = periodo ?? [];
	if (!dataInicial || !dataFinal) return null;
	return [dataInicial, dataFinal];
}

/**
 * Extrai uma mensagem de erro amigável ao usuário de vários tipos de erro.
 *
 * Esta função lida com diferentes cenários de erro:
 * - HTTPError: Retorna o código de status e o texto do status.
 * - NetworkError (TypeError): Retorna uma mensagem em português sobre problemas de rede.
 * - Erro genérico: Retorna a mensagem de erro.
 * - Erros desconhecidos: Retorna uma mensagem padrão "Erro desconhecido".
 *
 * @param err - O objeto de erro a ser processado, pode ser de qualquer tipo.
 * @returns Uma string representando a mensagem de erro.
 */
export const getStatusErrorMessage = (err: unknown): string => {
	if (err instanceof HTTPError) return `${err.response.status}: ${err.response.statusText}`;

	if (err instanceof TypeError && err.message.includes("NetworkError"))
		return "Erro de rede. Verifique sua conexão e tente novamente.";

	if (err instanceof Error) return err.message;

	return "Erro desconhecido";
};

function normalizarSeparadoresNumero(valor: string): string {
	const possuiPonto = valor.includes(".");
	const possuiVirgula = valor.includes(",");

	if (possuiPonto && possuiVirgula) return valor.replaceAll(".", "").replaceAll(",", ".");
	if (possuiVirgula) return valor.replaceAll(",", ".");

	return valor;
}

export function parseNumberBR(value: unknown): number | null {
	if (value === null || value === undefined) return null;

	if (typeof value === "number") return Number.isFinite(value) ? value : null;

	if (typeof value === "string") {
		const s0 = value.trim();
		if (!s0) return null;

		// remove tudo que não seja dígito, ponto, vírgula ou sinal
		const s = s0.replaceAll(/[^\d.,-]/g, "");
		if (!s) return null;

		const normalized = normalizarSeparadoresNumero(s);
		const n = Number(normalized);
		return Number.isFinite(n) ? n : null;
	}

	return null;
}

/**
 * Converte com segurança um valor para uma representação de string.
 *
 * Se o valor já for uma string, retorna como está.
 * Caso contrário, tenta usar JSON.stringify, e se falhar, recorre a String(v).
 *
 * @param v - O valor a ser convertido em string.
 * @returns A representação em string do valor.
 */
export function safeStringify(v: unknown, space = 2): string {
	if (typeof v === "string") return v;

	try {
		const result = JSON.stringify(v, null, space);
		if (result !== undefined && result !== null) return result;
	} catch {
		// Ignorado
	}

	// Fallback: trate explicitamente primitivos não serializáveis; caso contrário, stringify.
	if (v === undefined) return "undefined";
	if (typeof v === "symbol") return v.toString();
	if (typeof v === "function") return v.toString();

	// neste ponto `v` ainda pode ser um objeto, mas JSON.stringify já falhou ou
	// não produziu nada, então usar Object.prototype.toString evita o aviso do TS
	return Object.prototype.toString.call(v);
}

/**
 * Converte o `codigo_qr` (base64) retornado por `/me/gerar-qrcode-otp`
 * em um `src` válido para `<img src="..." />`.
 *
 * - Se já vier no formato `data:image/...;base64,....`, retorna como está.
 * - Caso venha apenas o base64 cru, prefixa como PNG por padrão.
 */
export function qrCodeBase64ToImageSrc(base64: string, mimeType = "image/png") {
	const value = (base64 ?? "").trim();
	if (!value) return "";

	// Já é uma data URL
	if (/^data:image\/[a-zA-Z0-9+.-]+;base64,/.test(value)) return value;

	return `data:${mimeType};base64,${value}`;
}

/**
 * Junta o valor de uma propriedade de um array de objetos em uma string separada por um delimitador.
 * @param items - Array de objetos.
 * @param property - Propriedade a ser concatenada.
 * @param separator - Delimitador (opcional, padrão ', ').
 * @returns Uma string contendo os valores concatenados.
 */
export function joinProperty<T>(items: T[], property: keyof T, separator = ", "): string {
	return items
		.map((item) => {
			const value = item[property];
			if (value == null) return "";
			if (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
				return String(value);
			if (typeof value === "object") return JSON.stringify(value);
			return "[unknown]";
		})
		.join(separator);
}

/**
 * Mapeia uma propriedade específica de um array de objetos para um novo array.
 * @param items - Array de objetos.
 * @param property - Propriedade a ser mapeada.
 * @returns Um array contendo os valores da propriedade especificada.
 */
export function mapProperty<T, K extends keyof T>(items: T[], property: K): Array<T[K]> {
	return items.map((item) => item[property]);
}

/**
 * Analisa um valor desconhecido e tenta convertê-lo em um número decimal.
 * @param value - O valor a ser analisado.
 * @returns O número decimal analisado ou 0 se a conversão falhar.
 */
export function parseDecimal(value: unknown): number {
	if (typeof value === "number") return Number.isFinite(value) ? value : 0;
	if (typeof value !== "string") return 0;
	const trimmed = value.trim();
	if (!trimmed) return 0;
	const num = Number(trimmed.replaceAll(",", "."));
	return Number.isFinite(num) ? num : 0;
}

/**
 * Renders a label with an optional required indicator.
 * @param label - The label text to display.
 * @param required - Whether to display the required indicator. Defaults to `false`.
 * @returns A JSX fragment containing the label and optional required text in Portuguese.
 */
export const labelWithRequired = (label: string, required?: boolean) => (
	<>
		{label}
		{required && <span className="text-danger/80 text-sm font-semibold"> (obrigatório)</span>}
	</>
);

/**
 * Formata um percentual numérico em string pt-BR sempre com 2 casas (ex.: 33,34).
 * @param valor - O valor numérico do percentual.
 * @returns A string formatada com duas casas decimais no padrão brasileiro.
 */
export function formatarPercentual(valor: number): string {
	return formatNumberBR(valor, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

/**
 * Converte um *preset* de período em um range de datas no formato ISO `YYYY-MM-DD`.
 *
 * - Retorna uma tupla `[start, end]` quando o preset é conhecido.
 * - Retorna `null` quando o preset é desconhecido.
 *
 * Regras por preset:
 * - `today`: início e fim = hoje
 * - `yesterday`: início e fim = ontem
 * - `thisWeek`: segunda-feira (início) até domingo (fim), considerando `weekStartsOn: 1`
 * - `thisMonth`: primeiro dia do mês atual até o último dia do mês atual
 * - `lastMonth`: primeiro dia do mês anterior até o último dia do mês anterior
 *
 * Observações:
 * - O cálculo usa a data/hora atual (`new Date()`).
 * - O formato é produzido por `dateToISODateOnly`, ou seja, apenas a parte de data (`YYYY-MM-DD`).
 * - O resultado depende do fuso horário local do ambiente (browser/Node).
 *
 * @param preset - Identificador do período desejado.
 *
 * @returns
 * - `[startISO, endISO]` (tupla) quando o preset é válido.
 * - `null` quando o preset não é suportado.
 *
 * @example
 * ```ts
 * presetToRangeDate('today');      // ['2026-01-12', '2026-01-12']
 * presetToRangeDate('last15days'); // ['2025-12-29', '2026-01-12'] (exemplo)
 * presetToRangeDate('yesterday');  // ['2026-01-11', '2026-01-11']
 * presetToRangeDate('thisWeek');   // ['2026-01-12', '2026-01-18'] (exemplo)
 * presetToRangeDate('thisMonth');  // ['2026-01-01', '2026-01-31'] (exemplo)
 * presetToRangeDate('lastMonth');  // ['2025-12-01', '2025-12-31'] (exemplo)
 * presetToRangeDate('thisYear');   // ['2026-01-01', '2026-12-31'] (exemplo)
 * presetToRangeDate('lastYear');   // ['2025-01-01', '2025-12-31'] (exemplo)
 * presetToRangeDate('foo');        // null
 * ```
 */
export const presetToRangeDate = (preset: DATE_PRESETS_TYPE) => {
	const now = new Date();
	switch (preset) {
		case "today": {
			const iso = dateToISODateOnly(now);
			return [iso, iso] as const;
		}
		case "yesterday": {
			const d = subDays(now, 1);
			const iso = dateToISODateOnly(d);
			return [iso, iso] as const;
		}
		case "last7days": {
			const start = subDays(now, 6);
			return [dateToISODateOnly(start), dateToISODateOnly(now)] as const;
		}
		case "last15days": {
			const start = subDays(now, 14);
			return [dateToISODateOnly(start), dateToISODateOnly(now)] as const;
		}
		case "thisWeek": {
			const start = startOfWeek(now, { weekStartsOn: 0 });
			const end = endOfWeek(now, { weekStartsOn: 0 });
			return [dateToISODateOnly(start), dateToISODateOnly(end)] as const;
		}
		case "thisMonth": {
			const start = startOfMonth(now);
			const end = endOfMonth(now);
			return [dateToISODateOnly(start), dateToISODateOnly(end)] as const;
		}
		case "lastMonth": {
			const base = subMonths(now, 1);
			const start = startOfMonth(base);
			const end = endOfMonth(base);
			return [dateToISODateOnly(start), dateToISODateOnly(end)] as const;
		}
		case "thisMonthUntilNow": {
			const start = startOfMonth(now);
			const end = now;
			return [dateToISODateOnly(start), dateToISODateOnly(end)] as const;
		}
		case "thisYear": {
			const start = new Date(now.getFullYear(), 0, 1);
			const end = new Date(now.getFullYear(), 11, 31);
			return [dateToISODateOnly(start), dateToISODateOnly(end)] as const;
		}
		case "lastYear": {
			const start = new Date(now.getFullYear() - 1, 0, 1);
			const end = new Date(now.getFullYear() - 1, 11, 31);
			return [dateToISODateOnly(start), dateToISODateOnly(end)] as const;
		}
		default:
			return null;
	}
};

const VALUE_POSITION_CLASS = {
	left: "text-left",
	center: "text-center",
	right: "text-right",
} as const;

type FormatCurrencyWithColorOptions = {
	readonly rawValue: string;
	readonly formatter: (value: number) => string;
	readonly inserirCorValorPositivo?: boolean;
	readonly inverterCorValor?: boolean;
	readonly positionValue?: keyof typeof VALUE_POSITION_CLASS;
};

/**
 * Formata um valor de moeda com estilos de cor condicionais baseado no valor.
 * Valores negativos recebem a classe 'text-danger', valores zero recebem a classe 'text-muted'.
 *
 * @param options - Configuração do valor, formatador, destaque positivo e alinhamento
 * @returns Um elemento JSX span com o valor formatado e className apropriado
 */
export function formatCurrencyWithColor({
	rawValue,
	formatter,
	inserirCorValorPositivo = false,
	inverterCorValor = false,
	positionValue = "right",
}: Readonly<FormatCurrencyWithColorOptions>): JSX.Element {
	const value = parseFloat(rawValue || "0");
	const formatted = formatter(value);
	const isNegative = inverterCorValor ? value > 0 : value < 0;
	const isZero = value === 0;
	const isPositive = inverterCorValor ? value < 0 : value > 0;

	let className = `block w-full ${VALUE_POSITION_CLASS[positionValue]}`;
	if (isNegative) className += " text-danger font-medium";
	else if (isZero) className += " text-muted-foreground font-[450]";
	else if (isPositive && inserirCorValorPositivo) className += " text-success font-bold";

	return <span className={className}>{formatted}</span>;
}

/**
 * Retorna um array com valores únicos a partir do array de entrada.
 *
 * @template T - O tipo dos elementos do array.
 * @param arr - O array de entrada do qual serão extraídos os valores únicos.
 * @returns Um novo array contendo apenas os elementos únicos do array de entrada.
 */
export function getUniqueValues<T>(arr: T[]): T[] {
	return Array.from(new Set(arr));
}

/**
 * Formata um valor como percentual no padrão pt-BR.
 *
 * Diferente do `Intl.NumberFormat` com `style: "percent"`,
 * esta função não multiplica o valor por 100.
 *
 * @param value - Valor a ser formatado. Ex.: `50`, `"50"` ou `"50,5"` para `50,00%` / `50,50%`.
 * @returns Percentual formatado com duas casas decimais. Ex.: `50,00%`.
 */
export function formatPercentage(value: unknown, options?: { decimal?: boolean }): string {
	const n = parseNumberBR(value) ?? 0;
	const percentage = options?.decimal ? n * 100 : n;

	return `${formatNumberBR(percentage, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}%`;
}

export type CurrencyConversionDirection = "api-to-form-cents" | "form-cents-to-api";
/**
 * Converte valores monetários entre o formato da API e o formato usado nos inputs do formulário.
 *
 * api-to-form-cents:
 * - "103.39" -> "10339"
 * - "103,39" -> "10339"
 * - 103.39   -> "10339"
 * - "0.00"   -> "0"
 *
 * form-cents-to-api:
 * - "10339" -> "103.39"
 * - "5600"  -> "56"
 * - "5650"  -> "56.50"
 * - "5"     -> "0.05"
 */
export function converterValorMoeda(
	value: string | number | null | undefined,
	direction: CurrencyConversionDirection,
): string | undefined {
	if (direction === "api-to-form-cents") {
		const parsedValue = parseNumberBR(value);

		if (parsedValue === null) return undefined;

		return String(Math.round(parsedValue * 100));
	}

	const digits = String(value ?? "").replaceAll(/\D/g, "");

	if (!digits) return undefined;

	const padded = digits.padStart(3, "0");
	const integerPart = padded.slice(0, -2).replace(/^0+(?=\d)/, "") || "0";
	const decimalPart = padded.slice(-2);

	return decimalPart === "00" ? integerPart : `${integerPart}.${decimalPart}`;
}

/**
 * Calcula a quantidade de dias de um período baseado em duas datas ISO.
 *
 * @param period Um par opcional `[início, fim]` em strings no formato ISO (`YYYY-MM-DD`).
 *               Se não fornecido, assume-se um par de strings vazias.
 * @returns O número de dias no intervalo, contando ambos os extremos.
 *          - Se ambas as datas forem válidas e o fim for igual ou posterior ao início,
 *            retorna a diferença em dias +1.
 *          - Se o fim for anterior ao início ou alguma data for inválida, retorna 0.
 *          - Se apenas a data de início for válida, retorna o número de dias do mês
 *            correspondente.
 *          - Se nenhuma data válida for fornecida, retorna o número de dias do mês atual.
 */
export function resolvePeriodDays(period?: [string, string]): number {
	const [start, end] = period ?? ["", ""];

	const s = start ? parseISO(start) : null;
	const e = end ? parseISO(end) : null;

	if (s && e && isValid(s) && isValid(e)) {
		const diff = differenceInCalendarDays(e, s);
		return diff >= 0 ? diff + 1 : 0; // inclusivo
	}

	// Fallbacks:
	if (s && isValid(s)) return getDaysInMonth(s);
	return getDaysInMonth(new Date());
}

export function safeNumber(valor: unknown): number {
	return typeof valor === "number" && Number.isFinite(valor) ? valor : 0;
}

export function clampNumber(valor: number, minimo: number, maximo: number): number {
	return Math.min(Math.max(valor, minimo), maximo);
}

type PropsTituloComTooltip = {
	readonly titulo: string;
	readonly tooltip: JSX.Element;
};
export function TituloComTooltip({ titulo, tooltip }: Readonly<PropsTituloComTooltip>) {
	return (
		<Tooltip arrow placement="top-start" title={tooltip}>
			<span className="inline-flex cursor-help items-center gap-1 font-semibold">
				<span>{titulo}</span>
				<FuseSvgIcon size={14}>heroicons-outline:information-circle</FuseSvgIcon>
			</span>
		</Tooltip>
	);
}
